import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react'; // Fix the import
import uShapeCutter from '../utils/uShapeCutter';
import { removeProductBackground } from '../utils/backgroundRemover';

// Add CSS for range slider styling
const sliderCSS = `
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #2D8C88;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(45, 140, 136, 0.3);
  }

  input[type="range"]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #2D8C88;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(45, 140, 136, 0.3);
  }

  input[type="range"]::-webkit-slider-track {
    background: #e0e0e0;
    height: 6px;
    border-radius: 3px;
  }

  input[type="range"]::-moz-range-track {
    background: #e0e0e0;
    height: 6px;
    border-radius: 3px;
    border: none;
  }

  input[type="range"]:focus {
    outline: none;
  }

  input[type="range"]:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px rgba(45, 140, 136, 0.2);
  }

  input[type="range"]:focus::-moz-range-thumb {
    box-shadow: 0 0 0 3px rgba(45, 140, 136, 0.2);
  }

  /* Clean Switch styles */
  .switch-container {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
  }

  .switch-container input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .switch-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.3);
    transition: .3s;
    border-radius: 22px;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .switch-slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  input:checked + .switch-slider {
    background-color: #2D8C88;
  }

  input:checked + .switch-slider:before {
    transform: translateX(18px);
  }

  input:disabled + .switch-slider {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Inject CSS (only once)
if (typeof document !== 'undefined' && !document.getElementById('wrist-size-slider-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'wrist-size-slider-styles';
  styleElement.textContent = sliderCSS;
  document.head.appendChild(styleElement);
}

const Tryon = ({ onBackToHome }) => {
  // Refs for DOM elements
  const videoRef = useRef(null);
  const capturedImageRef = useRef(null);
  const canvasRef = useRef(null);

  // State variables
  const [isCaptured, setIsCaptured] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isRightHand, setIsRightHand] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [activeTab, setActiveTab] = useState('Watches');
  const [showHandGuide, setShowHandGuide] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showDisclaimerPopup, setShowDisclaimerPopup] = useState(true);
  const [isUsingModelImage, setIsUsingModelImage] = useState(false);

  // URL parameters state
  const [urlParams, setUrlParams] = useState({
    image: null,
    client: null,
    size: null,
    type: null
  });

  // Analytics tracking state
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [behaviorMetrics, setBehaviorMetrics] = useState({
    timeToFirstInteraction: null,
    productSwitches: 0,
    backgroundRemovalSuccess: false,
    handDetectionSuccess: false,
    totalInteractions: 0,
    cameraInitSuccess: false,
    engagementScore: 0,
    scrollDepth: 0,
    mouseMovements: 0,
    hoverEvents: [],
    gestureEvents: [],
    featureUsage: {
      cameraToggle: 0,
      productRotation: 0,
      sizeAdjustment: 0,
      colorChange: 0,
      screenshot: 0,
      share: 0,
      zoom: 0
    },
    exitIntent: {
      detected: false,
      timestamp: null,
      beforeConversion: false
    },
    attentionMetrics: {
      focusTime: 0,
      blurEvents: 0,
      returnEvents: 0,
      idleTime: 0
    }
  });

  // Enhanced tracking state
  const [performanceMetrics, setPerformanceMetrics] = useState({
    frameRate: { average: 0, min: 0, max: 0, drops: 0 },
    memoryUsage: { used: 0, total: 0 },
    networkMetrics: {},
    renderMetrics: {},
    resourceLoadTimes: []
  });
  const [qualityMetrics, setQualityMetrics] = useState({
    handDetectionAccuracy: 0,
    backgroundRemovalQuality: 0,
    productFitAccuracy: 0,
    userSatisfactionScore: 0,
    technicalIssues: []
  });
  const [conversionFunnel, setConversionFunnel] = useState({
    viewedProduct: true,
    initiatedTryOn: false,
    completedTryOn: false,
    sharedResult: false,
    addedToCart: false,
    proceededToCheckout: false,
    completedPurchase: false
  });

  // Watch size customization state
  const [userGender, setUserGender] = useState('men'); // Single gender role
  const [userWristSize, setUserWristSize] = useState(50); // Default wrist size in mm
  const [showWristSizeModal, setShowWristSizeModal] = useState(false); // Mobile-friendly modal

  // Autocapture state variables
  const [isAutoCaptureEnabled, setIsAutoCaptureEnabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isHandInPosition, setIsHandInPosition] = useState(false);
  const [isCountdownActive, setIsCountdownActive] = useState(false);

  // Add new state for panel position
  const [panelPosition, setPanelPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const panelRef = useRef(null);

  // Realistic watch data with actual dimensions (in millimeters)
  const watches = [
    {
      name: "Classic Black",
      path: "watches/watch_1.png",
      // Rolex Submariner style - 40mm case
      caseDiameter: 41, // mm
      caseThickness: 12.5, // mm
      totalWidth: 42, // mm (including crown)
      totalHeight: 47, // mm (lug to lug)
      dialDiameter: 31, // mm (visible dial)
      type: "dress",
      dialSize: 40
    },
    {
      name: "Silver Chrono",
      path: "watches/watch_2.png",
      // Omega Speedmaster style - 42mm case
      caseDiameter: 42, // mm
      caseThickness: 13.2, // mm
      totalWidth: 44, // mm
      totalHeight: 48.5, // mm
      dialDiameter: 33, // mm
      type: "sport",
      dialSize: 42
    },
    {
      name: "Gold Luxury",
      path: "watches/watch_3.png",
      // Patek Philippe Calatrava style - 38mm case
      caseDiameter: 39, // mm
      caseThickness: 8.5, // mm
      totalWidth: 39, // mm
      totalHeight: 45, // mm
      dialDiameter: 30, // mm
      type: "luxury",
      dialSize: 38
    },
    {
      name: "Sport Blue",
      path: "watches/watch_6.png",
      // Apple Watch style - 44mm case
      caseDiameter: 41, // mm (width)
      caseThickness: 10.7, // mm
      totalWidth: 44, // mm
      totalHeight: 38, // mm (height - rectangular)
      dialDiameter: 35, // mm (screen diagonal)
      type: "smartwatch",
      dialSize: 44
    },
    {
      name: "Minimalist",
      path: "watches/watch_5.png",
      // Daniel Wellington style - 36mm case
      caseDiameter: 36, // mm
      caseThickness: 6, // mm
      totalWidth: 37, // mm
      totalHeight: 43, // mm
      dialDiameter: 28, // mm
      type: "minimalist",
      dialSize: 36
    },
    {
      name: "Rose Gold",
      path: "watches/watch_4.png",
      // Michael Kors style - 39mm case
      caseDiameter: 44, // mm
      caseThickness: 11, // mm
      totalWidth: 41, // mm
      totalHeight: 46, // mm
      dialDiameter: 31, // mm
      type: "fashion",
      dialSize: 41
    }
  ];

  const bracelets = [
    { name: "Silver Chain", path: "bracelets/bracelet_1.png" },
    { name: "Gold Bangle", path: "bracelets/bracelet_2.png" },
    { name: "Leather Wrap", path: "bracelets/bracelet_3.png" },
    { name: "Diamond Tennis", path: "bracelets/bracelet_4.png" },
    { name: "Beaded Stone", path: "bracelets/bracelet_5.png" },
    { name: "Charm Bracelet", path: "bracelets/bracelet_6.png" }
  ];

  // Enhanced background removal function with proper bracelet processing sequence
  const removeBackground = async (imgElement, productType = 'watch') => {
    try {
      let processedImageUrl = imgElement.src;

      // For bracelets: Apply U-shape cutter → Background removal → Vertical flip
      if (productType === 'bracelet') {
        try {
          // Step 1: Apply U-shape cutter first
          processedImageUrl = await uShapeCutter(imgElement.src);
          console.log('U-shape cutting applied successfully for bracelet');

          // Step 2: Remove background after U-shape cutting
          processedImageUrl = await removeProductBackground(processedImageUrl, productType);
          console.log('Background removal applied after U-shape cutting');

          // Step 3: Apply vertical flip based on hand detection (will be handled in transform)
          imgElement.src = processedImageUrl;

        } catch (error) {
          console.warn('Bracelet processing sequence failed:', error);
          // Fallback to basic processing
          await basicBackgroundRemoval(imgElement, productType);
        }
      } else {
        // For watches: Just apply background removal
        const finalProcessedUrl = await removeProductBackground(processedImageUrl, productType);
        imgElement.src = finalProcessedUrl;
      }

      // Apply product-specific styling that preserves all colors
      imgElement.style.filter = 'none';
      imgElement.style.mixBlendMode = 'normal';
      imgElement.style.opacity = '1';

      console.log(`Background removal completed for ${productType}`);

    } catch (error) {
      console.warn('Enhanced background removal failed:', error);
      // Fallback to basic background removal
      await basicBackgroundRemoval(imgElement, productType);
    }
  };

  // Fallback basic background removal
  const basicBackgroundRemoval = async (imgElement, productType) => {
    try {
      console.log(`Applying fallback background removal for ${productType}`);

      if (productType === 'bracelet') {
        // Apply U-shape cutter for bracelets
        const uShapedImage = await uShapeCutter(imgElement.src);
        imgElement.src = uShapedImage;
      }

      // Basic background removal with conservative settings
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = imgElement.naturalWidth;
      canvas.height = imgElement.naturalHeight;
      ctx.drawImage(imgElement, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Process each pixel - only remove very pure white backgrounds
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Only remove very white pixels (more conservative for watches)
        const threshold = productType === 'watch' ? 252 : 248;
        if (r > threshold && g > threshold && b > threshold &&
            Math.abs(r - g) < 3 && Math.abs(g - b) < 3) {
          data[i + 3] = 0; // Make transparent
        }
      }

      ctx.putImageData(imageData, 0, 0);
      imgElement.src = canvas.toDataURL('image/png');

      console.log(`Fallback background removal completed for ${productType}`);
    } catch (error) {
      console.error('Basic background removal failed:', error);
    }
  };

  // Hand detection logic from tryon.js
  const detectHandOrientation = (imageData) => {
    // Simple heuristic for demo purposes - can be enhanced with ML models
    return Math.random() > 0.5;
  };

  // Initialize camera
  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 30 }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      // Demo mode fallback
      if (capturedImageRef.current) {
        capturedImageRef.current.src = "sample-hand.jpg";
        capturedImageRef.current.style.display = "block";
      }
      console.log("Camera not available - demo mode");
      setIsCaptured(true);
      setShowProductSelection(true);
    }
  };

  // Capture current frame
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  };



  // Parse URL parameters on component mount
  useEffect(() => {
    const parseUrlParams = () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = {
        image: urlSearchParams.get('image'),
        client: urlSearchParams.get('client'),
        size: urlSearchParams.get('size'),
        type: urlSearchParams.get('type'),
        name: urlSearchParams.get('name')
      };

      console.log('Parsed URL parameters:', params);
      setUrlParams(params);

      // If image URL is provided, set it as selected product and skip product selection
      if (params.image) {
        try {
          // Validate the image URL
          const imageUrl = new URL(params.image);

          const productType = params.type || 'watches';
          const defaultSize = productType === 'bracelets' ? 15 : 42;
          const actualSize = parseInt(params.size) || defaultSize;

          setSelectedProduct({
            name: "Custom Product",
            path: params.image,
            caseDiameter: productType === 'watches' ? actualSize : null,
            braceletWidth: productType === 'bracelets' ? actualSize : null,
            caseThickness: productType === 'watches' ? 12 : null,
            totalWidth: actualSize,
            totalHeight: productType === 'watches' ? actualSize * 1.15 : actualSize * 3,
            dialDiameter: productType === 'watches' ? actualSize * 0.75 : null,
            type: productType,
            clientId: params.client
          });
          setShowProductSelection(false);

          console.log('Product loaded from URL:', {
            image: params.image,
            client: params.client,
            size: params.size
          });
        } catch (error) {
          console.error('Invalid image URL provided:', params.image);
          // Show product selection if URL is invalid
          setShowProductSelection(true);
        }
      }
    };

    parseUrlParams();
  }, []);

  // Start analytics session when URL parameters are loaded
  useEffect(() => {
    if (urlParams.client && urlParams.image) {
      console.log('Starting analytics session with params:', urlParams);
      startAnalyticsSession();

      // Initialize enhanced tracking
      const cleanup = initializeEnhancedTracking();

      // Mark try-on as initiated
      setConversionFunnel(prev => ({
        ...prev,
        initiatedTryOn: true
      }));

      // End session when user leaves the page
      const handleBeforeUnload = () => {
        endAnalyticsSession('abandoned');
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          endAnalyticsSession('abandoned');
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        cleanup();
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        endAnalyticsSession('abandoned');
      };
    } else {
      console.log('Missing required parameters for analytics:', {
        hasClient: !!urlParams.client,
        hasImage: !!urlParams.image
      });
    }
  }, [urlParams]);

  // Detect mobile device and set viewport height for Chrome mobile
  useEffect(() => {
    const checkDevice = () => {
      const isMobileDevice = window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
      setIsDesktop(!isMobileDevice);
    };

    // Fix viewport height for Chrome mobile
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    checkDevice();
    setVH();

    window.addEventListener('resize', () => {
      checkDevice();
      setVH();
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        setVH();
      }, 100);
    });

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Initialize camera when component mounts
  useEffect(() => {
    if (typeof window !== 'undefined' && navigator.mediaDevices) {
      initCamera();
    }
    // Show disclaimer popup when component first loads
    setShowDisclaimerPopup(true);
  }, []);

  // Realistic sizing configuration
  // Average adult wrist circumference: 165mm (men), 155mm (women)
  // Average wrist width when viewed from top: ~55-65mm
  // SVG guide circle represents the wrist area (120px diameter in 800px viewBox)
  // This means the circle represents approximately 60mm in real life

  // Universal wrist size configuration
  const DEFAULT_WRIST_SIZE = 50; // mm - ideal wrist width from top view
  const MIN_WRIST_SIZE = 35; // mm - minimum wrist width
  const MAX_WRIST_SIZE = 65; // mm - maximum wrist width
  const ASSUMED_DIAL_SIZE = 42; // mm - assumed real dial size for initial scaling

  // Default wrist sizes by gender (top view width in mm)
  const DEFAULT_WRIST_SIZES = {
    men: 50    // mm - average men's wrist width from top view
  };

  // Add wrist size adjustment constant
  const WRIST_SIZE_OFFSET = 10; // mm - subtract this from input wrist size for correct fitting
  const MIN_ADJUSTED_WRIST_SIZE = 37; // Minimum adjusted wrist size before scaling stops

  const SVG_WRIST_CIRCLE_DIAMETER = 120; // SVG circle diameter in viewBox units
  const SVG_VIEWBOX_WIDTH = 800; // SVG viewBox width
  const SVG_VIEWBOX_HEIGHT = 600; // SVG viewBox height

  // Size configuration - Increased for better visibility
  const WATCH_WIDTH = 25; // percentage of container width
  const BRACELET_WIDTH = 15; // percentage of container width
  const WATCH_HEIGHT = 38; // percentage of container height
  const BRACELET_HEIGHT = 55; // percentage of container height - INCREASED for better visibility

  // Test: Try changing WATCH_HEIGHT to 50 to see a much taller watch
  // Test: Try changing WATCH_HEIGHT to 15 to see a shorter watch
  // Test: Try changing BRACELET_HEIGHT to 70 to see a much taller bracelet
  // Test: Try changing BRACELET_HEIGHT to 20 to see a shorter bracelet
  // Note: Scale is now dynamic - smaller height = smaller scale, larger height = larger scale

  // Modify calculateWatchDimensions to use adjusted wrist size
  const calculateWatchDimensions = (watch, containerWidth, containerHeight) => {
    // Get the default wrist size for the current gender
    const defaultWristSize = DEFAULT_WRIST_SIZES[userGender];

    // Adjust the wrist size by subtracting the offset, but don't go below MIN_ADJUSTED_WRIST_SIZE
    const adjustedWristSize = Math.max(userWristSize - WRIST_SIZE_OFFSET, MIN_ADJUSTED_WRIST_SIZE);

    // Calculate INVERSE scaling factor - smaller wrist = larger watch, larger wrist = smaller watch
    const inverseWristSizeRatio = defaultWristSize / adjustedWristSize;

    // Calculate the scale factor from real world to SVG coordinates using adjusted wrist size
    const mmToSvgScale = SVG_WRIST_CIRCLE_DIAMETER / adjustedWristSize;

    // Convert watch dimensions to SVG units
    const watchWidthSvg = watch.totalWidth * mmToSvgScale;
    const watchHeightSvg = watch.totalHeight * mmToSvgScale;
    const dialDiameterSvg = watch.dialDiameter * mmToSvgScale;

    // Convert SVG units to container percentages
    const watchWidthPercent = (watchWidthSvg / SVG_VIEWBOX_WIDTH) * 100;
    const watchHeightPercent = (watchHeightSvg / SVG_VIEWBOX_HEIGHT) * 100;
    const dialDiameterPercent = (dialDiameterSvg / SVG_VIEWBOX_WIDTH) * 100;

    // Calculate positioning - watches should be positioned on the wrist circle
    const positionX = 50; // Center horizontally (400/800 = 50%)
    const positionY = 50; // Center vertically (300/600 = 50%)

    return {
      width: Math.max(watchWidthPercent, 8), // Minimum 8% for visibility
      height: Math.max(watchHeightPercent, 10), // Minimum 10% for visibility
      dialDiameter: dialDiameterPercent,
      positionX,
      positionY,
      scale: Math.min(watchWidthPercent / 15, watchHeightPercent / 20) * inverseWristSizeRatio,
      realWidth: watch.totalWidth,
      realHeight: watch.totalHeight,
      caseDiameter: watch.caseDiameter,
      wristSizeRatio: inverseWristSizeRatio,
      adjustedWristSize
    };
  };

  // Calculate watch position based on hand orientation and anatomy
  const getWatchPosition = (watchData, isRightHand) => {
    const baseDimensions = calculateWatchDimensions(watchData, 400, 600);

    // Adjust position based on hand orientation
    // Watches are typically worn on the top of the wrist
    let adjustedX = baseDimensions.positionX;
    let adjustedY = baseDimensions.positionY - 2; // Slightly higher on the wrist

    // For right hand, watch might be positioned slightly differently
    if (isRightHand) {
      adjustedX = baseDimensions.positionX + 1; // Slight adjustment for right hand
    }

    // Calculate scale to match SVG shape height
    const svgHeight = 300; // Height of the wrist/forearm area in SVG
    const watchHeight = watchData.totalHeight;
    const scaleToFitHeight = svgHeight / watchHeight;

    // Adjust for different watch types
    switch (watchData.type) {
      case 'smartwatch':
        // Smart watches are often worn higher on the wrist
        adjustedY -= 1;
        break;
      case 'luxury':
        // Luxury watches might be positioned more precisely
        adjustedY -= 0.5;
        break;
      case 'sport':
        // Sport watches might be worn slightly looser
        adjustedY += 0.5;
        break;
      default:
        break;
    }

    return {
      ...baseDimensions,
      positionX: adjustedX,
      positionY: adjustedY,
      scale: Math.min(baseDimensions.scale, scaleToFitHeight) // Ensure watch doesn't exceed SVG height
    };
  };

  // No static product data - products come from URL parameters or client integration

  // Enhanced tracking functions
  const initializeEnhancedTracking = () => {
    // Mouse movement tracking
    let mouseDistance = 0;
    let lastMousePos = { x: 0, y: 0 };

    const handleMouseMove = (e) => {
      const distance = Math.sqrt(
        Math.pow(e.clientX - lastMousePos.x, 2) +
        Math.pow(e.clientY - lastMousePos.y, 2)
      );
      mouseDistance += distance;
      lastMousePos = { x: e.clientX, y: e.clientY };

      setBehaviorMetrics(prev => ({
        ...prev,
        mouseMovements: mouseDistance
      }));
    };

    // Scroll depth tracking
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      setBehaviorMetrics(prev => ({
        ...prev,
        scrollDepth: Math.max(prev.scrollDepth, scrollPercent)
      }));

      trackInteraction('scroll', { depth: scrollPercent, position: scrollTop });
    };

    // Focus/blur tracking for attention metrics
    let focusStartTime = Date.now();
    let isPageFocused = true;

    const handleFocus = () => {
      if (!isPageFocused) {
        setBehaviorMetrics(prev => ({
          ...prev,
          attentionMetrics: {
            ...prev.attentionMetrics,
            returnEvents: prev.attentionMetrics.returnEvents + 1
          }
        }));
        focusStartTime = Date.now();
        isPageFocused = true;
      }
    };

    const handleBlur = () => {
      if (isPageFocused) {
        const focusTime = Date.now() - focusStartTime;
        setBehaviorMetrics(prev => ({
          ...prev,
          attentionMetrics: {
            ...prev.attentionMetrics,
            focusTime: prev.attentionMetrics.focusTime + focusTime,
            blurEvents: prev.attentionMetrics.blurEvents + 1
          }
        }));
        isPageFocused = false;
      }
    };

    // Exit intent detection
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        setBehaviorMetrics(prev => ({
          ...prev,
          exitIntent: {
            detected: true,
            timestamp: new Date(),
            beforeConversion: !conversionFunnel.completedPurchase
          }
        }));
        trackInteraction('exit_intent', { timestamp: new Date() });
      }
    };

    // Performance monitoring
    const monitorPerformance = () => {
      if ('memory' in performance) {
        const memory = performance.memory;
        setPerformanceMetrics(prev => ({
          ...prev,
          memoryUsage: {
            used: memory.usedJSHeapSize,
            total: memory.totalJSHeapSize
          }
        }));
      }

      // Network information
      if ('connection' in navigator) {
        const connection = navigator.connection;
        setPerformanceMetrics(prev => ({
          ...prev,
          networkMetrics: {
            connectionType: connection.type,
            downlink: connection.downlink,
            rtt: connection.rtt,
            effectiveType: connection.effectiveType
          }
        }));
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('mouseleave', handleMouseLeave);

    // Start performance monitoring
    monitorPerformance();
    const performanceInterval = setInterval(monitorPerformance, 5000);

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearInterval(performanceInterval);
    };
  };

  // Get user's IP address
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Could not get user IP:', error);
      return null;
    }
  };

  // Analytics tracking functions
  const startAnalyticsSession = async () => {
    if (!urlParams.client || !urlParams.image) {
      console.warn('No client ID or image provided for analytics');
      return;
    }

    try {
      const pageLoadStartTime = performance.now();
      const userIP = await getUserIP();

      const sessionData = {
        clientId: urlParams.client,
        productId: urlParams.image,
        productName: urlParams.name || selectedProduct?.name || 'Custom Product',
        productCategory: urlParams.type || 'watches',
        device: {
          type: window.innerWidth <= 768 ? 'mobile' : window.innerWidth <= 1024 ? 'tablet' : 'desktop',
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          os: navigator.platform,
          browser: navigator.userAgent.split(' ').pop(),
          userAgent: navigator.userAgent
        },
        location: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          referrer: document.referrer,
          userIP: userIP
        },
        behaviorMetrics: {
          timeToFirstInteraction: null,
          cameraInitSuccess: false,
          handDetectionSuccess: false,
          backgroundRemovalSuccess: false,
          productSwitches: 0,
          productViewTimes: [{
            productId: urlParams.image,
            duration: 0
          }],
          ...behaviorMetrics
        },
        performanceMetrics: {
          pageLoadTime: null,
          apiResponseTimes: [],
          errors: [],
          ...performanceMetrics
        },
        qualityMetrics,
        conversionFunnel
      };

      console.log('Sending analytics session data:', sessionData);

      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const apiUrl = `${baseUrl.replace(/\/+$/, '')}/api/analytics/session`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionData),
      });

      if (response.ok) {
        const result = await response.json();
        setSessionId(result.sessionId);
        setSessionStartTime(new Date());
        
        // Record page load time
        const pageLoadTime = performance.now() - pageLoadStartTime;
        trackPerformanceMetric('pageLoadTime', pageLoadTime);
        
        console.log('Analytics session started:', result.sessionId);
      } else {
        const errorData = await response.json();
        console.error('Failed to start analytics session:', errorData);
        trackError('session_start', errorData.message);
      }
    } catch (error) {
      console.error('Failed to start analytics session:', error);
      trackError('session_start', error.message);
    }
  };

  const trackInteraction = (type, data = {}) => {
    const interaction = {
      type,
      timestamp: new Date(),
      data
    };

    // Update behavior metrics based on interaction type
    if (type === 'camera_init') {
      updateBehaviorMetric('cameraInitSuccess', true);
    } else if (type === 'hand_detection') {
      updateBehaviorMetric('handDetectionSuccess', true);
    } else if (type === 'background_removal') {
      updateBehaviorMetric('backgroundRemovalSuccess', true);
    } else if (type === 'product_switch') {
      updateBehaviorMetric('productSwitches', (prev) => prev + 1);
    }

    // Enhanced interaction data
    interaction.position = data.position || { x: 0, y: 0 };
    interaction.element = data.element || '';
    interaction.duration = data.duration || 0;
    interaction.intensity = data.intensity || 0;
    interaction.sequence = interactions.length + 1;
    interaction.context = data.context || '';

    // Update feature usage metrics using the new updateBehaviorMetric function
    if (type === 'screenshot') {
      updateBehaviorMetric('featureUsage.screenshot', (prev) => prev + 1);
      setConversionFunnel(prev => ({
        ...prev,
        completedTryOn: true
      }));
    } else if (type === 'share') {
      updateBehaviorMetric('featureUsage.share', (prev) => prev + 1);
      setConversionFunnel(prev => ({
        ...prev,
        sharedResult: true
      }));
    } else if (type === 'zoom') {
      updateBehaviorMetric('featureUsage.zoom', (prev) => prev + 1);
    } else if (type === 'size_adjustment') {
      updateBehaviorMetric('featureUsage.sizeAdjustment', (prev) => prev + 1);
    } else if (type === 'color_change') {
      updateBehaviorMetric('featureUsage.colorChange', (prev) => prev + 1);
    } else if (type === 'product_switch') {
      updateBehaviorMetric('featureUsage.productRotation', (prev) => prev + 1);
    } else if (type === 'capture') {
      updateBehaviorMetric('featureUsage.cameraToggle', (prev) => prev + 1);
    }

    // Track time to first interaction if not set
    if (!behaviorMetrics.timeToFirstInteraction && sessionStartTime) {
      const timeToFirstInteraction = (new Date() - sessionStartTime) / 1000;
      updateBehaviorMetric('timeToFirstInteraction', timeToFirstInteraction);
    }

    // Calculate engagement score
    const engagementScore = Math.min(100,
      (interactions.length * 5) +
      (behaviorMetrics.featureUsage.screenshot * 10) +
      (behaviorMetrics.featureUsage.share * 15) +
      (behaviorMetrics.scrollDepth * 0.2) +
      (behaviorMetrics.mouseMovements * 0.001)
    );

    setBehaviorMetrics(prev => ({
      ...prev,
      engagementScore: Math.round(engagementScore)
    }));

    setInteractions(prev => [...prev, interaction]);
    console.log('Enhanced interaction tracked:', interaction);
  };

  const trackPerformanceMetric = (metric, value) => {
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const apiUrl = `${baseUrl.replace(/\/+$/, '')}/api/analytics/session/${sessionId}/performance`;

    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metric,
        value,
        timestamp: new Date()
      }),
    }).catch(error => {
      console.error('Failed to track performance metric:', error);
    });
  };

  const trackError = (type, message) => {
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const apiUrl = `${baseUrl.replace(/\/+$/, '')}/api/analytics/session/${sessionId}/error`;

    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        message,
        timestamp: new Date()
      }),
    }).catch(error => {
      console.error('Failed to track error:', error);
    });
  };

  const updateBehaviorMetric = (metric, value) => {
    // Update local state
    setBehaviorMetrics(prev => {
      const newMetrics = { ...prev };

      // Handle nested properties like 'featureUsage.screenshot'
      if (metric.includes('.')) {
        const [parent, child] = metric.split('.');
        if (newMetrics[parent]) {
          newMetrics[parent] = { ...newMetrics[parent], [child]: value };
        }
      } else {
        newMetrics[metric] = typeof value === 'function' ? value(prev[metric]) : value;
      }

      return newMetrics;
    });

    // Send to backend if session exists
    if (sessionId) {
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const apiUrl = `${baseUrl.replace(/\/+$/, '')}/api/analytics/session/${sessionId}/behavior`;

      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          metric,
          value: typeof value === 'function' ? value(behaviorMetrics[metric] || 0) : value,
          timestamp: new Date()
        }),
      }).catch(error => {
        console.error('Failed to update behavior metric:', error);
      });
    }
  };

  const endAnalyticsSession = async (outcome = 'abandoned') => {
    if (!sessionId) return;

    try {
      const endTime = new Date();
      const duration = sessionStartTime ? Math.floor((endTime - sessionStartTime) / 1000) : 0;

      const updateData = {
        endTime,
        duration,
        outcome,
        interactions,
        behaviorMetrics,
        performanceMetrics,
        qualityMetrics,
        conversionFunnel
      };

      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const apiUrl = `${baseUrl.replace(/\/+$/, '')}/api/analytics/session/${sessionId}`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        console.log('Analytics session ended:', outcome, 'Duration:', duration, 'seconds');
      } else {
        const errorData = await response.json();
        console.error('Failed to end analytics session:', errorData);
      }
    } catch (error) {
      console.error('Failed to end analytics session:', error);
    }
  };





  // Detect if arm and wrist are within the SVG guide area
  const detectHandInPosition = () => {
    if (!videoRef.current || !canvasRef.current) return false;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get the video container dimensions to calculate the guide area
    const videoContainer = video.parentElement;
    const containerRect = videoContainer.getBoundingClientRect();

    // Calculate the actual video display area (accounting for object-fit: cover)
    const videoAspect = video.videoWidth / video.videoHeight;
    const containerAspect = containerRect.width / containerRect.height;

    let displayWidth, displayHeight, offsetX, offsetY;

    if (videoAspect > containerAspect) {
      // Video is wider - height fills container, width is cropped
      displayHeight = containerRect.height;
      displayWidth = displayHeight * videoAspect;
      offsetX = (displayWidth - containerRect.width) / 2;
      offsetY = 0;
    } else {
      // Video is taller - width fills container, height is cropped
      displayWidth = containerRect.width;
      displayHeight = displayWidth / videoAspect;
      offsetX = 0;
      offsetY = (displayHeight - containerRect.height) / 2;
    }

    // Calculate the guide areas in canvas coordinates
    // SVG viewBox is 800x600
    // Main rectangle: x="320" y="150" width="160" height="300" (wrist/forearm area)
    // Circle: cx="400" cy="300" r="60" (hand area)

    const scaleX = canvas.width / displayWidth;
    const scaleY = canvas.height / displayHeight;

    // Wrist/forearm area (rectangle)
    const rectX = Math.max(0, ((320 / 800) * displayWidth - offsetX) * scaleX);
    const rectY = Math.max(0, ((150 / 600) * displayHeight - offsetY) * scaleY);
    const rectWidth = Math.min(canvas.width - rectX, ((160 / 800) * displayWidth) * scaleX);
    const rectHeight = Math.min(canvas.height - rectY, ((300 / 600) * displayHeight) * scaleY);

    // Hand area (circle)
    const circleX = Math.max(0, ((340 / 800) * displayWidth - offsetX) * scaleX); // Adjusted for circle bounds
    const circleY = Math.max(0, ((240 / 600) * displayHeight - offsetY) * scaleY); // Adjusted for circle bounds
    const circleWidth = Math.min(canvas.width - circleX, ((120 / 800) * displayWidth) * scaleX); // Circle diameter
    const circleHeight = Math.min(canvas.height - circleY, ((120 / 600) * displayHeight) * scaleY); // Circle diameter

    try {
      // Check wrist/forearm area (rectangle)
      const rectImageData = ctx.getImageData(rectX, rectY, rectWidth, rectHeight);
      const rectData = rectImageData.data;

      // Check hand area (circle approximation)
      const circleImageData = ctx.getImageData(circleX, circleY, circleWidth, circleHeight);
      const circleData = circleImageData.data;

      let rectSkinPixels = 0;
      let rectTotalPixels = 0;
      let circleSkinPixels = 0;
      let circleTotalPixels = 0;

      // Enhanced skin tone detection
      const isSkinTone = (r, g, b) => {
        // Multiple skin tone ranges to cover different skin colors
        const condition1 = r > 95 && g > 40 && b > 20 && r > g && r > b && Math.abs(r - g) > 15;
        const condition2 = r > 220 && g > 210 && b > 170 && Math.abs(r - g) < 15 && Math.abs(g - b) < 15;
        const condition3 = r > 120 && g > 80 && b > 50 && r > g && g > b;
        const condition4 = r > 180 && g > 120 && b > 90 && r > g && g >= b;

        return condition1 || condition2 || condition3 || condition4;
      };

      // Analyze rectangle area (wrist/forearm)
      for (let i = 0; i < rectData.length; i += 4) {
        const r = rectData[i];
        const g = rectData[i + 1];
        const b = rectData[i + 2];

        if (isSkinTone(r, g, b)) {
          rectSkinPixels++;
        }
        rectTotalPixels++;
      }

      // Analyze circle area (hand)
      for (let i = 0; i < circleData.length; i += 4) {
        const r = circleData[i];
        const g = circleData[i + 1];
        const b = circleData[i + 2];

        if (isSkinTone(r, g, b)) {
          circleSkinPixels++;
        }
        circleTotalPixels++;
      }

      // Calculate skin ratios
      const rectSkinRatio = rectSkinPixels / rectTotalPixels;
      const circleSkinRatio = circleSkinPixels / circleTotalPixels;

      // Both wrist/forearm AND hand areas must have sufficient skin tone presence
      // Wrist area needs at least 20% skin tone (forearm/wrist)
      // Hand area needs at least 25% skin tone (hand/fingers)
      const wristInPosition = rectSkinRatio > 0.20;
      const handInPosition = circleSkinRatio > 0.25;

      return wristInPosition && handInPosition;

    } catch (error) {
      console.warn('Hand detection error:', error);
      return false;
    }
  };

  // Apply product to watch position with dynamic scaling
  const applyProductToWatchPosition = (productPath, productType) => {
    // Clear any existing product first
    setSelectedProduct(null);

    // Find the watch data for dial size
    const watchData = watches.find(w => w.path === productPath);

    // Small delay to ensure the old product is removed before adding new one
    setTimeout(() => {
      setSelectedProduct({
        path: productPath,
        dialSize: watchData?.dialSize || 40, // Default to 40mm if not found
        dimensions: watchData // Pass full watch dimensions for scaling
      });
    }, 50);
  };

  // Screenshot functionality with tracking
  const takeScreenshot = () => {
    if (!capturedImageRef.current) return;

    try {
      // Create a canvas to combine the captured image with the product overlay
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = capturedImageRef.current.naturalWidth || 800;
      canvas.height = capturedImageRef.current.naturalHeight || 600;

      // Draw the captured image
      ctx.drawImage(capturedImageRef.current, 0, 0, canvas.width, canvas.height);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tryon-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Track screenshot
        trackInteraction('screenshot', {
          timestamp: new Date(),
          productId: urlParams.image,
          sessionDuration: sessionStartTime ? (new Date() - sessionStartTime) / 1000 : 0
        });

        // Mark as completed try-on
        setConversionFunnel(prev => ({
          ...prev,
          completedTryOn: true
        }));
      }, 'image/png');
    } catch (error) {
      console.error('Screenshot failed:', error);
      trackError('screenshot_failed', error.message);
    }
  };

  // Zoom functionality with tracking
  const handleZoom = (zoomLevel) => {
    trackInteraction('zoom', {
      zoomLevel,
      timestamp: new Date(),
      element: 'product_overlay'
    });
  };

  // Share functionality with tracking
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Check out my virtual try-on!',
          text: 'I tried on this product virtually',
          url: window.location.href
        });

        trackInteraction('share', {
          method: 'native_share',
          timestamp: new Date()
        });

        setConversionFunnel(prev => ({
          ...prev,
          sharedResult: true
        }));
      } else {
        // Fallback to copying URL
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');

        trackInteraction('share', {
          method: 'copy_link',
          timestamp: new Date()
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
      trackError('share_failed', error.message);
    }
  };

  // Handle Try On Model button click
  const handleTryOnModel = () => {
    if (!isCaptured) {
      // Load the model hand image from public folder
      const modelImagePath = '/imgs/hand/hand.png'; // Using the hand.jpg from public folder

      if (capturedImageRef.current) {
        capturedImageRef.current.src = modelImagePath;
        capturedImageRef.current.style.display = 'block';

        // Wait for image to load, then detect wrist and apply try-on logic
        capturedImageRef.current.onload = () => {
          // Simulate wrist detection on the model image
          detectWristOnModelImage();
        };
      }

      setIsCaptured(true);
      setIsUsingModelImage(true);
      // Only show product selection if no product is provided via URL
      if (!urlParams.image) {
        setShowProductSelection(true);
      }
      setShowHandGuide(false);

      // Track try-on model interaction
      trackInteraction('capture', {
        method: 'model_tryon',
        hasProduct: !!urlParams.image,
        timestamp: new Date()
      });

      // Set hand orientation for model (assume left hand for consistency)
      setIsRightHand(false);
    }
  };

  // Detect wrist position on model image and apply scaling
  const detectWristOnModelImage = () => {
    if (!capturedImageRef.current) return;

    try {
      // Create a canvas to analyze the model image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = capturedImageRef.current;

      canvas.width = img.naturalWidth || 800;
      canvas.height = img.naturalHeight || 600;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // For model image with closed palm, position the watch/bracelet on the palm area
      // Since it's a closed hand, we need to detect the palm center rather than wrist
      const palmPosition = detectPalmPositionOnModel(canvas);

      if (palmPosition) {
        console.log('Palm position detected on model image:', palmPosition);

        // Store the palm position for product placement
        window.modelPalmPosition = palmPosition;

        // Mark behavior metrics for successful hand detection
        setBehaviorMetrics(prev => ({
          ...prev,
          handDetectionSuccess: true,
          palmDetected: true
        }));

        // Track successful palm detection
        trackInteraction('hand_detection', {
          method: 'model_image_palm',
          success: true,
          palmPosition: palmPosition,
          timestamp: new Date()
        });
      } else {
        console.warn('Could not detect palm position on model image');
      }

    } catch (error) {
      console.warn('Model image palm detection error:', error);
      trackError('model_palm_detection', error.message);
    }
  };

  // Detect palm position on closed hand model image
  const detectPalmPositionOnModel = (canvas) => {
    try {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // For a closed hand model, we need to find the wrist area where the watch/bracelet should go
      // The wrist is typically at the bottom of the hand, where the arm connects

      // Scan from bottom up to find the wrist area (narrowest part of the arm)
      let wristY = null;
      let wristX = canvas.width * 0.5; // Start from center
      let minWidth = canvas.width;

      // Scan the bottom third of the image to find the wrist
      const startY = Math.floor(canvas.height * 0.7); // Start from 70% down
      const endY = canvas.height - 20; // Stop 20px from bottom

      for (let y = startY; y < endY; y += 2) {
        let leftEdge = null;
        let rightEdge = null;

        // Scan horizontally to find the edges of the arm/wrist
        for (let x = 0; x < canvas.width; x++) {
          const pixelIndex = (y * canvas.width + x) * 4;
          const r = data[pixelIndex];
          const g = data[pixelIndex + 1];
          const b = data[pixelIndex + 2];
          const alpha = data[pixelIndex + 3];

          // Check if pixel is part of the hand/arm (not background)
          // Adjust threshold based on the hand image characteristics
          const isHandPixel = alpha > 100 && (r < 200 || g < 200 || b < 200);

          if (isHandPixel && leftEdge === null) {
            leftEdge = x;
          }
          if (isHandPixel) {
            rightEdge = x;
          }
        }

        // If we found both edges, check if this is the narrowest point (wrist)
        if (leftEdge !== null && rightEdge !== null) {
          const armWidth = rightEdge - leftEdge;

          if (armWidth < minWidth && armWidth > 40) { // Reasonable wrist width
            minWidth = armWidth;
            wristY = y;
            wristX = leftEdge + armWidth / 2;
          }
        }
      }

      // If we couldn't detect the wrist automatically, use default position
      if (!wristY) {
        wristY = canvas.height * 0.05; // 85% down from top (moved down a bit)
        wristX = canvas.width * 0.5; // Center horizontally
        console.log('Using default wrist position for closed hand model');
      }

      // Calculate the wrist area for product placement
      // Make the watch bigger for model hand since it's larger than SVG
      const wristBounds = {
        x: wristX - 80, // Increased wrist area width for bigger watch
        y: wristY - 40, // Increased wrist area height for bigger watch
        width: 160, // Increased from 120 to make watch bigger
        height: 60, // Increased from 60 to make watch bigger
        centerX: wristX,
        centerY: wristY + 10, // Move center down a bit
        confidence: wristY ? 0.9 : 0.7,
        isModelHand: true // Flag to indicate this is model hand for scaling adjustments
      };

      console.log('Detected wrist area for closed hand model:', wristBounds);

      return wristBounds;

    } catch (error) {
      console.error('Error detecting wrist position on closed hand:', error);
      // Return default wrist position with bigger size for model hand
      return {
        x: canvas.width * 0.5 - 80,
        y: canvas.height * 0.85 - 40,
        width: 160,
        height: 80,
        centerX: canvas.width * 0.5,
        centerY: canvas.height * 0.85 + 10,
        confidence: 0.7,
        isModelHand: true
      };
    }
  };

  // Handle capture button click
  const handleCapture = () => {
    if (!isCaptured) {
      const capturedDataUrl = captureFrame();
      if (capturedImageRef.current && capturedDataUrl) {
        capturedImageRef.current.src = capturedDataUrl;
        capturedImageRef.current.style.display = 'block';
      }
      setIsCaptured(true);
      setIsUsingModelImage(false);
      // Only show product selection if no product is provided via URL
      if (!urlParams.image) {
        setShowProductSelection(true);
      }
      setShowHandGuide(false);

      // Track capture interaction
      trackInteraction('capture', {
        method: 'manual_capture',
        hasProduct: !!urlParams.image,
        timestamp: new Date()
      });

      // Detect hand orientation
      if (canvasRef.current && videoRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        setIsRightHand(detectHandOrientation(imageData));
      }
    } else {
      // Only show product selection if no product is provided via URL
      if (!urlParams.image) {
        setShowProductSelection(true);
      }
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (capturedImageRef.current) {
      capturedImageRef.current.style.display = 'none';
    }
    setIsCaptured(false);
    setSelectedProduct(null);
    setIsRightHand(false);
    setShowProductSelection(false);
    setShowWristSizeModal(false);
    setShowHandGuide(true);
    setIsUsingModelImage(false); // Reset model image state
  };

  // Handle gender selection
  const handleGenderChange = (gender) => {
    setUserGender('men'); // Single gender role
    setUserWristSize(50); // Set initial size for men
    trackInteraction('size_adjustment', {
      type: 'gender_change',
      gender: 'men',
      newWristSize: 50
    });
  };

  // Handle wrist size change
  const handleWristSizeChange = (size) => {
    setUserWristSize(size);
    trackInteraction('size_adjustment', {
      type: 'wrist_size_change',
      size
    });
  };

  // Handle tab change
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  // Handle autocapture switch toggle
  const handleAutoCaptureToggle = () => {
    const newState = !isAutoCaptureEnabled;
    setIsAutoCaptureEnabled(newState);

    // Reset states when turning off
    if (!newState) {
      setIsCountdownActive(false);
      setCountdown(0);
      setIsHandInPosition(false);
    }
  };

  // Reset autocapture when going back
  const handleBackWithReset = () => {
    setIsAutoCaptureEnabled(false);
    setIsCountdownActive(false);
    setCountdown(0);
    setIsHandInPosition(false);
    setShowWristSizeModal(false);
    setUserWristSize(50); // Default to men's size
    setIsUsingModelImage(false); // Reset model image state
    // Go back to wherever user came from instead of just resetting
    onBackToHome();
  };

  // Get current products based on active tab
  const getCurrentProducts = () => {
    return activeTab === 'Watches' ? watches : bracelets;
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    applyProductToWatchPosition(product.path, activeTab);
  };

  // Handle disclaimer popup close
  const handleDisclaimerClose = () => {
    setShowDisclaimerPopup(false);
  };

  // Hand position monitoring effect (only when autocapture is enabled)
  useEffect(() => {
    if (!isAutoCaptureEnabled || isCaptured) return;

    const interval = setInterval(() => {
      const handInPosition = detectHandInPosition();
      setIsHandInPosition(handInPosition);

      // Only start countdown if hand is properly positioned
      if (handInPosition && !isCountdownActive && countdown === 0) {
        setIsCountdownActive(true);
      }

      // Stop countdown if hand moves out of position
      if (!handInPosition && isCountdownActive) {
        setIsCountdownActive(false);
        setCountdown(0);
      }
    }, 150); // Check every 150ms for better performance

    return () => clearInterval(interval);
  }, [isAutoCaptureEnabled, isCaptured, isCountdownActive, countdown]);

  // Countdown effect (starts automatically when hand is in position)
  useEffect(() => {
    if (!isCountdownActive || isCaptured) {
      setCountdown(0);
      return;
    }

    // Only start countdown if hand is still in position
    if (!isHandInPosition) {
      setIsCountdownActive(false);
      setCountdown(0);
      return;
    }

    // Start countdown
    setCountdown(3);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        // Double-check hand position before continuing countdown
        if (!isHandInPosition) {
          clearInterval(countdownInterval);
          setIsCountdownActive(false);
          return 0;
        }

        if (prev <= 1) {
          // Countdown finished - trigger capture
          clearInterval(countdownInterval);
          setIsCountdownActive(false);
          setIsAutoCaptureEnabled(false); // Turn off autocapture after capture
          handleCapture();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isCountdownActive, isCaptured, isHandInPosition]);

  // Hand position monitoring effect (only when autocapture is enabled)
  useEffect(() => {
    if (!isAutoCaptureEnabled || isCaptured) return;

    const interval = setInterval(() => {
      const handInPosition = detectHandInPosition();
      setIsHandInPosition(handInPosition);

      // Only start countdown if hand is properly positioned
      if (handInPosition && !isCountdownActive && countdown === 0) {
        setIsCountdownActive(true);
      }

      // Stop countdown if hand moves out of position
      if (!handInPosition && isCountdownActive) {
        setIsCountdownActive(false);
        setCountdown(0);
      }
    }, 150); // Check every 150ms for better performance

    return () => clearInterval(interval);
  }, [isAutoCaptureEnabled, isCaptured, isCountdownActive, countdown]);

  // Countdown effect (starts automatically when hand is in position)
  useEffect(() => {
    if (!isCountdownActive || isCaptured) {
      setCountdown(0);
      return;
    }

    // Only start countdown if hand is still in position
    if (!isHandInPosition) {
      setIsCountdownActive(false);
      setCountdown(0);
      return;
    }

    // Start countdown
    setCountdown(3);

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        // Double-check hand position before continuing countdown
        if (!isHandInPosition) {
          clearInterval(countdownInterval);
          setIsCountdownActive(false);
          return 0;
        }

        if (prev <= 1) {
          // Countdown finished - trigger capture
          clearInterval(countdownInterval);
          setIsCountdownActive(false);
          setIsAutoCaptureEnabled(false); // Turn off autocapture after capture
          handleCapture();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isCountdownActive, isCaptured, isHandInPosition]);



  // Add touch gesture handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    // Only allow dragging down
    if (diff > 0) {
      setPanelPosition(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // If dragged more than 100px down, close the panel
    if (panelPosition > 100) {
      setShowProductSelection(false);
    }
    
    // Reset position
    setPanelPosition(0);
  };

  // Add click outside handler for modals
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showWristSizeModal && !e.target.closest('.modal-content')) {
        setShowWristSizeModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showWristSizeModal]);

  // Desktop QR Code Component
  const DesktopQRCode = () => {
    // Generate dynamic QR code URL based on URL parameters or default
    const generateQRValue = () => {
      const websiteUrl = process.env.REACT_APP_WEBSITE_URL || 'https://viatryon.com';
      const baseUrl = `${websiteUrl}/tryon`;

      // If we have URL parameters, include them in the QR code
      if (urlParams.image || urlParams.client || urlParams.size || urlParams.type) {
        const params = new URLSearchParams();
        if (urlParams.image) params.append('image', urlParams.image);
        if (urlParams.client) params.append('client', urlParams.client);
        if (urlParams.size) params.append('size', urlParams.size);
        if (urlParams.type) params.append('type', urlParams.type);
        return `${baseUrl}?${params.toString()}`;
      }

      // Default QR code for general try-on
      return baseUrl;
    };

    const qrValue = generateQRValue();

    return (
      <div style={styles.desktopContainer}>
        <div style={styles.qrContainer}>
          <h2 style={styles.qrTitle}>Scan QR Code to Try On</h2>
          <p style={styles.qrSubtitle}>
            {urlParams.image
              ? "Scan to try on this specific product on your mobile device"
              : "Open this page on your mobile device to experience the virtual try-on feature"
            }
          </p>
          <div style={styles.qrWrapper}>
            <QRCodeSVG
              value={qrValue}
              size={256}
              level="H"
              bgColor="#FFFFFF"
              fgColor="#2D8C88"
            />
          </div>
          <p style={styles.qrLink}>{qrValue}</p>
          {urlParams.client && (
            <p style={styles.clientInfo}>Client: {urlParams.client}</p>
          )}
          <button
            style={styles.homeBtn}
            onClick={onBackToHome}
            aria-label="Home"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  };

  // Return desktop view if not on mobile
  if (isDesktop) {
    return <DesktopQRCode />;
  }

  // Update product selection panel JSX
  return (
    <div style={styles.container}>
      {/* Disclaimer Popup */}
      {showDisclaimerPopup && (
        <div style={styles.disclaimerOverlay}>
          <div style={styles.disclaimerPopup}>
            <div style={styles.disclaimerContent}>
              <h3 style={styles.disclaimerTitle}>Before You Start</h3>
              <div style={styles.disclaimerPoints}>
                <p style={styles.disclaimerPoint}>
                  Images shown are for demonstration purposes - actual size may vary
                </p>
                <p style={styles.disclaimerPoint}>
                  Position your wrist within the guide lines for better results
                </p>
              </div>
              <button
                style={styles.disclaimerButton}
                onClick={handleDisclaimerClose}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back Arrow Button - Top Left */}
      <button
        style={styles.backArrowBtn}
        onClick={() => window.history.back()}
        aria-label="Go Back"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
        </svg>
      </button>

      {/* Powered by ViaTryon Branding - Between back arrow and autocapture */}
      <div style={styles.brandingContainerBetween}>
        <img
          src="/imgs/logo-only.png"
          alt="ViaTryon Logo"
          style={{
            ...styles.brandingLogoTiny,
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
          }}
        />
        <div style={styles.brandingTextTiny}>
          <span style={{
            ...styles.poweredByTextTiny,
            color: isUsingModelImage ? '#000000' : styles.poweredByTextTiny.color
          }}>Powered by</span>
          <span style={{
            ...styles.viatryonTextTiny,
            color: isUsingModelImage ? '#000000' : styles.viatryonTextTiny.color
          }}>ViaTryon</span>
        </div>
      </div>

      <div style={styles.cameraContainer}>
        <video
          ref={videoRef}
          style={styles.cameraFeed}
          autoPlay
          playsInline
          muted
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <img
          ref={capturedImageRef}
          style={styles.capturedImage}
          alt="Captured hand"
        />

        {/* Clean Auto Capture Toggle */}
        {!isCaptured && (
          <div style={styles.autoCaptureToggle}>
            <span style={styles.autoCaptureLabel}>Auto</span>
            <label className="switch-container">
              <input
                type="checkbox"
                checked={isAutoCaptureEnabled}
                onChange={handleAutoCaptureToggle}
                disabled={isCountdownActive}
                aria-label="Toggle auto capture"
              />
              <span className="switch-slider"></span>
            </label>
          </div>
        )}

        {/* Countdown Display - Only visible during active countdown */}
        {isCountdownActive && (
          <div style={styles.countdownDisplay}>
            <div style={styles.countdownNumber}>{countdown}</div>
            <div style={styles.countdownText}>Auto capturing...</div>
          </div>
        )}

        {/* Simple Instruction Message - Clean and minimal */}
        {!isCaptured && !isCountdownActive && !isAutoCaptureEnabled && (
          <div style={styles.instructionContainer}>
            <div style={styles.instructionText}>
              Position your wrist within the guides
            </div>
          </div>
        )}

        {/* Status Messages */}
        {isAutoCaptureEnabled && !isHandInPosition && !isCountdownActive && (
          <div style={styles.statusMessageSmall}>
            <div style={styles.statusTextSmall}>Position your arm and wrist in the guide area</div>
            <div style={styles.statusSubtextSmall}>Countdown will start automatically when detected</div>
          </div>
        )}

        {isAutoCaptureEnabled && isHandInPosition && !isCountdownActive && (
          <div style={styles.statusMessage}>
            <div style={{...styles.statusText, backgroundColor: 'rgba(45, 140, 136, 0.9)'}}>
              Perfect! Starting countdown...
            </div>
          </div>
        )}

        {/* Hand Guide SVG */}
        {showHandGuide && (
          <div
            style={{
              ...styles.handGuide,
              opacity: isAutoCaptureEnabled && isHandInPosition ? 0.9 : 0.8,
              filter: isAutoCaptureEnabled && isHandInPosition
                ? 'drop-shadow(0 2px 8px rgba(45, 140, 136, 0.5))'
                : isAutoCaptureEnabled && !isHandInPosition
                  ? 'drop-shadow(0 2px 8px rgba(255, 107, 107, 0.5))'
                  : 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))'
            }}
            className={isMobile ? 'mobile-hand-guide' : ''}
            aria-hidden="true"
          >
            <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
              {/* Wrist guide lines */}
              <path
                d="M100 480 C 150 460, 200 450, 250 450 L 550 450 C 600 450, 650 460, 700 480"
                stroke={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d="M100 120 C 150 140, 200 150, 250 150 L 550 150 C 600 150, 650 140, 700 120"
                stroke={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
              />
              {/* Wrist/Forearm area (rectangle) */}
              <rect
                x="320"
                y="150"
                width="160"
                height="300"
                fill={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                opacity={isAutoCaptureEnabled && isHandInPosition ? "0.25" : "0.15"}
                rx="15"
                stroke={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                strokeWidth="2"
              />
              {/* Hand area (circle) */}
              <circle
                cx="400"
                cy="300"
                r="60"
                fill={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                opacity={isAutoCaptureEnabled && isHandInPosition ? "0.3" : "0.2"}
                stroke={
                  isAutoCaptureEnabled && isHandInPosition
                    ? "#2D8C88"
                    : isAutoCaptureEnabled && !isHandInPosition
                      ? "#ff6b6b"
                      : "white"
                }
                strokeWidth="2"
              />
              {/* Labels for clarity */}
              {isAutoCaptureEnabled && (
                <>
                  <text x="400" y="140" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                    WRIST & FOREARM
                  </text>
                  <text x="400" y="480" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
                    HAND
                  </text>
                </>
              )}
            </svg>
          </div>
        )}

        {/* Product Display - Only show after capture */}
        {selectedProduct && isCaptured && (
          <div style={{
            ...styles.productPosition,
            // Adjust positioning for model hand - move up/down and make bigger
            // Change '52%' to move up (lower %) or down (higher %)
            top: isUsingModelImage ? '52%' : '50%',
            transform: isUsingModelImage ? 'translate(-50%, -50%) scale(1.3)' : 'translate(-50%, -50%)',
            width: activeTab === 'Watches' ? `${WATCH_WIDTH}%` : `${BRACELET_WIDTH}%`,
            height: activeTab === 'Watches'
              ? (() => {
                  const defaultWristSize = DEFAULT_WRIST_SIZE;
                  const isLargeWrist = (userWristSize >= DEFAULT_WRIST_SIZE);

                  if (isLargeWrist) {
                    // For large wrists, increase height by 40% to allow exceeding SVG height
                    const sizeIncrease = (userWristSize - DEFAULT_WRIST_SIZE) / DEFAULT_WRIST_SIZE;
                    return `${WATCH_HEIGHT * (1 + sizeIncrease * 0.9)}%`;
                  }
                  return `${WATCH_HEIGHT}%`;
                })()
              : `${BRACELET_HEIGHT}%`,
            // Apply clipping for wrist sizes >= 50mm (men) and >= 45mm (women)
            clipPath: (() => {
              const isLargeWrist = (userWristSize >= DEFAULT_WRIST_SIZE);
              return activeTab === 'Watches' && isLargeWrist
                ? 'ellipse(220px 60px at 50% 50%)'
                : activeTab === 'Watches' && userWristSize < DEFAULT_WRIST_SIZE
                  ? 'ellipse(220px 60px at 50% 50%)'
                  : 'none';
            })(),
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={typeof selectedProduct === 'object' ? selectedProduct.path : selectedProduct}
                alt="Selected product"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transform: activeTab === 'Bracelets'
                    ? (() => {
                        // Use exact bracelet fitting logic with hand-based vertical rotation
                        const baseTransform = `rotate(90deg) scale(${BRACELET_HEIGHT / 35})`;

                        // Apply realistic bracelet positioning based on hand detection
                        // Rotate vertically but in opposite directions for left vs right hand
                        if (isUsingModelImage) {
                          // For left hand model: rotate vertically in opposite direction
                          return `${baseTransform} scaleY(1)`; // Opposite of current scaleY(-1)
                        } else if (isRightHand) {
                          // For right hand: rotate vertically one direction
                          return `${baseTransform} scaleY(-1)`;
                        } else {
                          // For left hand: rotate vertically in opposite direction to right hand
                          return `${baseTransform} scaleY(1)`;
                        }
                      })()
                    : (() => {
                        const defaultWristSize = DEFAULT_WRIST_SIZES[userGender];
                        const adjustedWristSize = Math.max(userWristSize - WRIST_SIZE_OFFSET, MIN_ADJUSTED_WRIST_SIZE);
                        const isLargeWrist = (userGender === 'men' && adjustedWristSize >= 50);

                        if (isLargeWrist) {
                          // For larger wrists, apply height scaling increase and allow exceeding SVG height
                          const sizeIncrease = (adjustedWristSize - defaultWristSize) / defaultWristSize;
                          const heightScale = 1 + (sizeIncrease * 0.4); // 40% height increase
                          const widthScale = defaultWristSize / adjustedWristSize; // Decrease width as wrist increases

                          return `scale(${(WATCH_HEIGHT / 25) * widthScale}) scaleX(${widthScale}) scaleY(${heightScale})`;
                        }

                        // For smaller wrists, use the original working logic with SVG height constraint
                        return `scale(${Math.min(
                          (WATCH_HEIGHT / 25) * (adjustedWristSize > defaultWristSize
                            ? defaultWristSize / adjustedWristSize
                            : defaultWristSize / adjustedWristSize),
                          300 / (selectedProduct.dimensions?.totalHeight || 47) // Scale to match SVG height
                        )}) scaleX(${adjustedWristSize > defaultWristSize
                          ? defaultWristSize / adjustedWristSize
                          : 1})`;
                      })(),
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
                }}
                onLoad={(e) => removeBackground(e.target, (urlParams.type === 'bracelets' || activeTab === 'Bracelets') ? 'bracelet' : 'watch')}
              />
              {((urlParams.type !== 'bracelets' && activeTab === 'Watches') || (!urlParams.type && activeTab === 'Watches')) && typeof selectedProduct === 'object' && (
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: 'rgba(45, 140, 136, 0.9)',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  zIndex: 2
                }}>
                  {selectedProduct.dialSize || selectedProduct.caseDiameter || urlParams.size || '42'}mm
                  {userWristSize !== DEFAULT_WRIST_SIZE && (
                    <span style={{
                      fontSize: '10px',
                      opacity: 0.8,
                      marginLeft: '4px'
                    }}>
                      {(() => {
                        const wristSizeRatio = DEFAULT_WRIST_SIZE / userWristSize;

                        let scalingPercentage;
                        if (userWristSize < DEFAULT_WRIST_SIZE) {
                          // Realistic scaling for smaller wrists
                          const sizeDifference = DEFAULT_WRIST_SIZE - userWristSize;
                          const maxSizeDifference = DEFAULT_WRIST_SIZE * 0.25;
                          const clampedDifference = Math.min(sizeDifference, maxSizeDifference);
                          const moderateScaleFactor = 1 + (clampedDifference / DEFAULT_WRIST_SIZE) * 0.6;
                          scalingPercentage = ((moderateScaleFactor - 1) * 100).toFixed(0);
                        } else {
                          // Standard scaling for larger wrists
                          scalingPercentage = ((wristSizeRatio - 1) * 100).toFixed(0);
                        }

                        // Add debug indicator for large wrists
                        const debugSuffix = userWristSize >= DEFAULT_WRIST_SIZE ? ' 🔥' : '';
                        return `(${userWristSize < DEFAULT_WRIST_SIZE ? '+' : ''}${scalingPercentage}%)${debugSuffix}`;
                      })()}
                    </span>
                  )}
                </div>
              )}
              {(urlParams.type === 'bracelets' || activeTab === 'Bracelets') && typeof selectedProduct === 'object' && (
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: 'rgba(45, 140, 136, 0.9)',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  zIndex: 2
                }}>
                  {selectedProduct.braceletWidth || urlParams.size || '15'}mm width
                </div>
              )}
            </div>
          </div>
        )}

        {/* Camera-style Capture Button - Only show before capture */}
        {!isCaptured && (
          <div style={styles.captureButtonContainer}>
            <div style={styles.captureButtonWrapper}>
              <button
                style={styles.captureBtn}
                className={isMobile ? 'mobile-capture-btn' : ''}
                onClick={handleCapture}
                aria-label="Capture"
              >
                <div style={styles.captureInner} className={isMobile ? 'mobile-inner-circle' : ''}></div>
              </button>
              <span style={styles.buttonLabel}>Capture</span>
            </div>

            {/* Try On Model Button */}
            <div style={styles.modelButtonWrapper}>
              <button
                style={styles.modelBtn}
                className={isMobile ? 'mobile-model-btn' : ''}
                onClick={handleTryOnModel}
                aria-label="Try on model"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M19,13H16.5L15.5,15H14L16,11H13V7H11V11H8L10,15H8.5L7.5,13H5C4.46,13 4,13.46 4,14V16C4,16.54 4.46,17 5,17H6V19C6,20.11 6.9,21 8,21H16C17.11,21 18,20.11 18,19V17H19C19.54,17 20,16.54 20,16V14C20,13.46 19.54,13 19,13M12,2A2,2 0 0,1 14,4A2,2 0 0,1 12,6A2,2 0 0,1 10,4A2,2 0 0,1 12,2Z"/>
                </svg>
              </button>
              <span style={styles.buttonLabel}>Try Model</span>
            </div>
          </div>
        )}

        {/* Reset Button - Only visible when captured */}
        {isCaptured && (
          <button
            style={styles.resetBtn}
            onClick={() => window.location.reload()}
            aria-label="Reset"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4 7.58 4 12S7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12S8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z"/>
            </svg>
          </button>
        )}
      </div>

      {/* Mobile Wrist Size Button - Top right corner */}
      {isCaptured && (
        <button
          style={styles.wristSizeFloatingBtn}
          className={isMobile ? 'mobile-btn' : ''}
          onClick={() => setShowWristSizeModal(true)}
          aria-label="Adjust wrist size"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
            <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
          </svg>
          <span style={styles.wristSizeText}>{userWristSize}mm</span>
        </button>
      )}

      {/* Wrist Size Modal - Mobile-friendly popup */}
      {showWristSizeModal && (
        <div 
          style={styles.modalOverlay} 
          onClick={() => setShowWristSizeModal(false)}
          className="modal-overlay"
        >
          <div 
            style={styles.wristSizeModal} 
            onClick={(e) => e.stopPropagation()}
            className="modal-content"
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Adjust Wrist Size</h3>
              <button
                style={styles.modalCloseBtn}
                onClick={() => setShowWristSizeModal(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <div style={styles.modalContent}>
              {/* Gender Selection - Single Role */}
              <div style={styles.genderSelection}>
                <button
                  style={{
                    ...styles.genderButton,
                    ...styles.genderButtonActive
                  }}
                  onClick={() => handleGenderChange('men')}
                >
                  Standard (50mm)
                </button>
              </div>

              {/* Wrist Size Slider */}
              <div style={styles.sliderContainer}>
                <label style={styles.sliderLabel}>
                  Wrist Size: {userWristSize}mm
                </label>
                <input
                  type="range"
                  min={40}
                  max={65}
                  value={userWristSize}
                  onChange={(e) => handleWristSizeChange(parseInt(e.target.value))}
                  style={styles.slider}
                />
                <div style={styles.sliderLabels}>
                  <span>40mm</span>
                  <span>65mm</span>
                </div>

                {/* Quick Size Presets */}
                <div style={styles.presetButtons}>
                  <button
                    style={styles.presetButton}
                    onClick={() => handleWristSizeChange(DEFAULT_WRIST_SIZE)}
                  >
                    Average ({DEFAULT_WRIST_SIZE}mm)
                  </button>
                  <button
                    style={styles.presetButton}
                    onClick={() => handleWristSizeChange(40)}
                  >
                    Small (40mm)
                  </button>
                  <button
                    style={styles.presetButton}
                    onClick={() => handleWristSizeChange(65)}
                  >
                    Large (65mm)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Selection Panel */}
      {showProductSelection && (
        <div
          ref={panelRef}
          style={{
            ...styles.productSelection,
            transform: `translateY(${panelPosition}px)`,
            touchAction: 'none'
          }}
          className={isMobile ? 'mobile-product-panel' : ''}
          aria-modal="true"
          role="dialog"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div 
            style={styles.dragHandle} 
            aria-hidden="true"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          {/* Only show tabs if no specific product type is provided via URL */}
          {!urlParams.type && (
            <div style={styles.productTabs}>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'Watches' ? styles.activeTab : {})
                }}
                onClick={() => handleTabChange('Watches')}
              >
                Watches
              </button>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'Bracelets' ? styles.activeTab : {})
                }}
                onClick={() => handleTabChange('Bracelets')}
              >
                Bracelets
              </button>
            </div>
          )}
          <div style={styles.productScroll} className="product-scroll">
            {getCurrentProducts().length > 0 ? (
              getCurrentProducts().map((product, index) => {
                // Simple null check only
                if (!product) return null;

                const isSelected = (typeof selectedProduct === 'object' ? selectedProduct?.path : selectedProduct) === product.path;

                return (
                  <button
                    key={index}
                    style={{
                      ...styles.productItem,
                      borderColor: isSelected ? '#2D8C88' : '#e9ecef',
                      backgroundColor: isSelected ? '#f0fffe' : '#ffffff'
                    }}
                    title={`${product.name} - ${product.caseDiameter || 'N/A'}mm`}
                    onClick={() => handleProductSelect(product)}
                    aria-label={`Select ${product.name} ${product.caseDiameter || 'N/A'}mm`}
                  >
                    <img
                      src={product.path}
                      alt={product.name}
                      style={styles.productImage}
                      onError={(e) => {
                        e.target.parentElement.style.display = 'none';
                      }}
                    />
                    <div style={styles.productLabel}>
                      <div style={styles.productName}>{product.name}</div>
                      {activeTab === 'Watches' && product.caseDiameter && (
                        <div style={styles.productSize}>{product.caseDiameter}mm</div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div style={styles.noProductsMessage}>
                <div style={styles.noProductsIcon}>📱</div>
                <div style={styles.noProductsTitle}>No Products Available</div>
                <div style={styles.noProductsText}>
                  This try-on experience is designed to be accessed through client websites with specific product parameters.
                </div>
                <div style={styles.noProductsSubtext}>
                  Please visit a client's product page and click the "Try On Virtually" button.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Styles object - Clean, modern design with improved mobile responsiveness
const styles = {
  container: {
    position: 'relative',
    height: 'calc(var(--vh, 1vh) * 100)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    color: '#333',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
  },
  cameraContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  cameraFeed: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: 'scaleX(1)' // Fix for Safari mirroring
  },
  capturedImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'none',
    WebkitTransform: 'scaleX(1)' // Fix for Safari mirroring
  },

  homeBtn: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    padding: '12px',
    borderRadius: '50%',
    fontSize: '20px',
    fontWeight: '700',
    cursor: 'pointer',
    zIndex: 10,
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    outline: 'none'
  },
  backBtn: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    padding: '12px',
    borderRadius: '50%',
    fontSize: '20px',
    fontWeight: '700',
    cursor: 'pointer',
    zIndex: 10,
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    outline: 'none'
  },
  switchContainer: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: '20px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  switchTrack: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    width: '60px',
    height: '32px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '16px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    zIndex: 9,
    transition: 'all 0.3s ease'
  },
  switchButton: {
    position: 'relative',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 11,
    margin: '2px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    outline: 'none',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  },
  switchLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)',
    marginTop: '4px',
    padding: '4px 12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    letterSpacing: '0.5px'
  },
  countdownDisplay: {
    position: 'absolute',
    top: '35%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 15,
    textAlign: 'center',
    pointerEvents: 'none',
    padding: '16px 24px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '20px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  countdownNumber: {
    fontSize: '72px',
    fontWeight: '900',
    color: '#2D8C88',
    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
    marginBottom: '8px',
    animation: 'pulse 1s ease-in-out'
  },
  countdownText: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)'
  },
  statusMessage: {
    position: 'absolute',
    top: '25%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 15,
    textAlign: 'center',
    pointerEvents: 'none',
    padding: '12px 20px',
    borderRadius: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  statusText: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)',
    backgroundColor: 'rgba(255, 107, 107, 0.9)',
    padding: '12px 20px',
    borderRadius: '25px',
    marginBottom: '8px',
    transition: 'all 0.3s ease'
  },
  statusSubtext: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'white',
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: '6px 12px',
    borderRadius: '15px'
  },
  handGuide: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: '400px',
    height: 'auto',
    opacity: 0.8,
    pointerEvents: 'none',
    zIndex: 5,
    filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
    WebkitFilter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
    transition: 'all 0.3s ease'
  },
productPosition: {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '25vw', // width controlled
  aspectRatio: '1 / 1.6', // maintain height-to-width ratio (adjust as needed)
  minWidth: '100px',
  minHeight: '160px', // fallback for unsupported aspect-ratio
  pointerEvents: 'none'
}
,
  captureButtonContainer: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '30px',
    zIndex: 10
  },
  captureButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  modelButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px'
  },
  buttonLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '4px 8px',
    borderRadius: '12px',
    whiteSpace: 'nowrap',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)'
  },
  captureBtn: {
    width: '80px',
    height: '80px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    outline: 'none',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  },
  captureInner: {
    width: '60px',
    height: '60px',
    backgroundColor: '#2D8C88',
    borderRadius: '50%',
    transition: 'all 0.2s ease'
  },
  modelBtn: {
    width: '60px',
    height: '60px',
    backgroundColor: 'rgba(45, 140, 136, 0.9)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    outline: 'none',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  },
  resetBtn: {
    position: 'absolute',
    bottom: '30px',
    right: '30px',
    width: '50px',
    height: '50px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 10,
    transition: 'all 0.2s ease',
    border: 'none',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
    outline: 'none',
    padding: 0
  },

  // Wrist Size Floating Button - Follows CSS mobile patterns
  wristSizeFloatingBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: '#2D8C88',
    color: 'white',
    padding: '12px 16px',
    borderRadius: '24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
    zIndex: 15,
    boxShadow: '0 2px 8px rgba(45, 140, 136, 0.3)',
    minHeight: '48px',
    minWidth: '48px',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  },
  wristSizeText: {
    fontSize: '12px',
    fontWeight: '600'
  },

  // Modal Styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '20px',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    touchAction: 'none'
  },
  wristSizeModal: {
    backgroundColor: 'white',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '95vw',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    margin: '10px',
    position: 'relative'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px 12px 20px',
    borderBottom: '1px solid #f0f0f0'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2D8C88',
    margin: 0
  },
  modalCloseBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#f5f5f5',
    color: '#666',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  modalContent: {
    padding: '16px 20px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxHeight: 'calc(80vh - 60px)'
  },

  // Mobile styles handled by CSS file
  wristSizeContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  },
  wristSizeTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2D8C88',
    margin: 0,
    textAlign: 'center'
  },
  wristSizeSubtitle: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
    textAlign: 'center',
    lineHeight: '1.4'
  },
  genderSelection: {
    display: 'flex',
    gap: '12px',
    width: '100%',
    maxWidth: '300px'
  },
  genderButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '2px solid #e0e0e0',
    backgroundColor: '#ffffff',
    color: '#666',
    outline: 'none'
  },
  genderButtonActive: {
    backgroundColor: '#2D8C88',
    color: '#ffffff',
    borderColor: '#2D8C88',
    boxShadow: '0 2px 8px rgba(45, 140, 136, 0.3)'
  },
  sliderContainer: {
    width: '100%',
    maxWidth: '300px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  sliderLabel: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  sizeChange: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#2D8C88',
    backgroundColor: 'rgba(45, 140, 136, 0.1)',
    padding: '2px 8px',
    borderRadius: '12px'
  },
  slider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    background: '#e0e0e0',
    outline: 'none',
    cursor: 'pointer',
    WebkitAppearance: 'none',
    appearance: 'none'
  },
  sliderLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#999',
    marginTop: '4px'
  },
  presetButtons: {
    display: 'flex',
    gap: '8px',
    width: '100%',
    maxWidth: '300px'
  },
  presetButton: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid #e0e0e0',
    backgroundColor: '#ffffff',
    color: '#666',
    outline: 'none'
  },
  continueButton: {
    width: '100%',
    maxWidth: '300px',
    padding: '14px 24px',
    backgroundColor: '#2D8C88',
    color: '#ffffff',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    outline: 'none',
    boxShadow: '0 4px 12px rgba(45, 140, 136, 0.3)'
  },

  // Fancy Popup Styles
  fancyPopupModal: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '380px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(45, 140, 136, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    margin: '10px',
    position: 'relative',
    border: '1px solid rgba(45, 140, 136, 0.1)'
  },
  fancyPopupHeader: {
    textAlign: 'center',
    padding: '32px 24px 24px 24px',
    background: 'linear-gradient(135deg, #2D8C88 0%, #1a6b67 100%)',
    color: 'white',
    position: 'relative'
  },
  fancyPopupTitle: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '8px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
  },
  fancyPopupSubtitle: {
    fontSize: '14px',
    fontWeight: '400',
    opacity: 0.9,
    letterSpacing: '0.5px'
  },
  fancyPopupContent: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  fancyPopupPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px'
  },
  fancyPointNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2D8C88 0%, #1a6b67 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(45, 140, 136, 0.3)'
  },
  fancyPointText: {
    fontSize: '15px',
    lineHeight: '1.5',
    color: '#333',
    flex: 1,
    paddingTop: '4px'
  },
  fancyOkButton: {
    position: 'relative',
    width: '100%',
    padding: '16px 24px',
    background: 'linear-gradient(135deg, #2D8C88 0%, #1a6b67 100%)',
    color: '#ffffff',
    borderRadius: '0 0 24px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: 'none',
    outline: 'none',
    overflow: 'hidden'
  },
  fancyButtonText: {
    position: 'relative',
    zIndex: 2
  },
  fancyButtonGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },

  // Clean Instruction Styles
  instructionContainer: {
    position: 'absolute',
    top: '28%', // moved further down from 20%
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 19,
    pointerEvents: 'none'
  },
  instructionText: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    textAlign: 'center',
    padding: '8px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '12px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)'
  },



  productSelection: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    padding: '16px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 20,
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
    border: 'none',
    transform: 'translateY(0)',
    transition: 'transform 0.3s ease-out',
    overflow: 'hidden',
    touchAction: 'none',
    willChange: 'transform',
    userSelect: 'none',
    WebkitUserSelect: 'none'
  },
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    color: '#666',
    cursor: 'pointer',
    zIndex: 21,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    border: 'none',
    outline: 'none',
    padding: 0
  },
  productTabs: {
    display: 'flex',
    marginBottom: '16px',
    backgroundColor: '#f0f0f0',
    borderRadius: '12px',
    padding: '4px',
    gap: '4px'
  },
  tab: {
    flex: 1,
    textAlign: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#666',
    outline: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  },
  activeTab: {
    backgroundColor: '#2D8C88',
    color: '#ffffff',
    boxShadow: '0 1px 4px rgba(45, 140, 136, 0.3)'
  },
  productScroll: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '12px',
    maxHeight: 'calc(85vh - 120px)',
    overflowY: 'auto',
    paddingBottom: '16px',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ddd #f5f5f5',
    WebkitOverflowScrolling: 'touch'
  },
  productItem: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1/1',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    padding: '8px',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation'
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '8px',
    backgroundColor: '#ffffff'
  },
  productLabel: {
    position: 'absolute',
    bottom: '3px',
    left: '3px',
    right: '3px',
    fontSize: '9px',
    color: '#666',
    textAlign: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '3px',
    padding: '3px 2px',
    overflow: 'hidden'
  },
  productName: {
    fontSize: '9px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    marginBottom: '1px'
  },
  productSize: {
    fontSize: '8px',
    fontWeight: '500',
    color: '#2D8C88',
    whiteSpace: 'nowrap'
  },
  dragHandle: {
    width: '40px',
    height: '4px',
    backgroundColor: '#e0e0e0',
    borderRadius: '2px',
    margin: '0 auto 12px',
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none'
  },

  desktopContainer: {
    position: 'relative',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: '20px'
  },
  qrContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '24px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%'
  },
  qrTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2D8C88',
    marginBottom: '16px'
  },
  qrSubtitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '32px',
    lineHeight: '1.5'
  },
  qrWrapper: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '16px',
    display: 'inline-block',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
  },
  qrLink: {
    fontSize: '14px',
    color: '#2D8C88',
    marginBottom: '32px',
    wordBreak: 'break-all'
  },
  clientInfo: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '16px',
    fontStyle: 'italic'
  },
  noProductsMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    height: '200px'
  },
  noProductsIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  noProductsTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '12px'
  },
  noProductsText: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.5',
    marginBottom: '8px',
    maxWidth: '280px'
  },
  noProductsSubtext: {
    fontSize: '12px',
    color: '#999',
    lineHeight: '1.4',
    maxWidth: '260px'
  },

  // Disclaimer Popup Styles
  disclaimerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)'
  },
  disclaimerPopup: {
    backgroundColor: 'white',
    borderRadius: '20px',
    padding: '0',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  disclaimerContent: {
    padding: '32px 24px',
    textAlign: 'center'
  },
  disclaimerTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2D8C88',
    marginBottom: '24px',
    margin: '0 0 24px 0'
  },
  disclaimerPoints: {
    marginBottom: '32px'
  },
  disclaimerPoint: {
    fontSize: '16px',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '16px',
    margin: '0 0 16px 0',
    textAlign: 'center'
  },
  disclaimerButton: {
    backgroundColor: '#2D8C88',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(45, 140, 136, 0.3)',
    ':hover': {
      backgroundColor: '#258A86',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 16px rgba(45, 140, 136, 0.4)'
    }
  },

  // Clean Auto Capture Toggle
  autoCaptureToggle: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 20,
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '16px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  autoCaptureLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)'
  },

  // Clean Branding Styles
  brandingContainer: {
    position: 'absolute',
    top: '80px',
    left: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    zIndex: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: '6px 12px',
    borderRadius: '16px',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)'
  },
  brandingLogo: {
    width: '22px',
    height: '22px',
    objectFit: 'contain',
    marginRight: '7px',
    display: 'inline-block',
    verticalAlign: 'middle'
  },
  brandingText: {
    fontSize: '11px',
    fontWeight: '500',
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)'
  },

  // Add new styles for smaller status message
  statusMessageSmall: {
    position: 'absolute',
    top: '13%', // move up so it's above the SVG shape
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 15,
    textAlign: 'center',
    pointerEvents: 'none',
    padding: '6px 12px',
    borderRadius: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
  },
  statusTextSmall: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)',
    backgroundColor: 'rgba(255, 107, 107, 0.8)',
    padding: '6px 12px',
    borderRadius: '15px',
    marginBottom: '4px',
    transition: 'all 0.3s ease'
  },
  statusSubtextSmall: {
    fontSize: '10px',
    fontWeight: '400',
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.7)',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '4px 8px',
    borderRadius: '10px'
  },

  // Clean Branding - Tangiblee style
  brandingContainerTangiblee: {
    position: 'absolute',
    top: '32px',
    left: '16px', // Moved more to the left
    display: 'flex',
    gap: '14px',
    zIndex: 15,
    backgroundColor: 'rgba(0,0,0,0.0)', // fully transparent
    padding: '0',
    borderRadius: '0',
    boxShadow: 'none',
  },
  brandingLogoTangiblee: {
    width: '45px', // Made logo slightly bigger
    height: '45px',
    objectFit: 'contain',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  brandingTextStacked: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1.2, // Increased line height for more vertical space
  },
  poweredByText: {
    fontSize: '16px', // Made text slightly bigger
    color: 'white',
    fontWeight: 400,
    opacity: 0.85,
    letterSpacing: '0.01em',
    marginBottom: '2px', // Increased spacing between texts
  },
  viatryonText: {
    fontSize: '28px', // Made text bigger
    color: 'white',
    fontWeight: 600,
    letterSpacing: '0.01em',
    marginTop: '0',
  },

  // Back Arrow Button - Top Left
  backArrowBtn: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    width: '44px',
    height: '44px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    outline: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    zIndex: 20
  },

  // Tiny Branding - Between back arrow and autocapture switch
  brandingContainerBetween: {
    position: 'absolute',
    top: '25px',
    left: '65px', // Moved more to the left
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    zIndex: 15,
    backgroundColor: 'rgba(0,0,0,0.0)', // fully transparent
    padding: '0',
    borderRadius: '0',
    boxShadow: 'none',
  },
  brandingLogoTiny: {
    width: '35px', // Made logo bigger
    height: '35px',
    objectFit: 'contain',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  brandingTextTiny: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1.2, // Increased line height for more vertical space
  },
  poweredByTextTiny: {
    fontSize: '14px', // Made text bigger
    color: 'white',
    fontWeight: 400,
    opacity: 0.85,
    letterSpacing: '0.01em',
    marginBottom: '2px', // Increased spacing between texts
  },
  viatryonTextTiny: {
    fontSize: '14px', // Made text bigger
    color: 'white',
    fontWeight: 600,
    letterSpacing: '0.01em',
    marginTop: '0',
  },
};

export default Tryon;
